import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, LockIcon } from 'lucide-react';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { FormError } from '../shared/FormError';
import { Spinner } from '../../ui/Spinner';
import { btnPrice, btnPriceAmount, btnPriceLabel, inputCls, labelCls } from '../../ui/styles';
import { founder, salon } from '../../data/content';
import { paymentOptionsFor } from '../../data/payments';
import { shopLabels } from '../../data/shopLabels';
import { plural } from '../../core/format/text';
import { postcodeExample } from '../../core/rules/contact';
import type { Country } from '../../core/types/requests';
import { countries, euroCents, fieldErrorId, fieldId, type AddressTextKey } from './checkoutRules';
import { CheckoutTrust } from './CheckoutTrust';
import { CheckoutPayBar } from './CheckoutPayBar';
import { useFieldChecks } from './useFieldChecks';
import type { CheckoutControl } from './useCheckout';
import type { LookupState } from './useAddressLookup';
type AddressField = {
  key: AddressTextKey;
  label: string;
  autoComplete: string;
  wide?: boolean;
  hint?: string;
  inputMode?: 'numeric' | 'text';
};
const addressFields: AddressField[] = [{
  key: 'firstName',
  label: 'Voornaam',
  autoComplete: 'given-name'
}, {
  key: 'lastName',
  label: 'Achternaam',
  autoComplete: 'family-name'
}, {
  key: 'postcode',
  label: 'Postcode',
  autoComplete: 'postal-code'
},
// Geen standaardwaarde voor alleen het huisnummer: address-line2 is de minst foute gok.
{
  key: 'houseNumber',
  label: 'Huisnummer',
  autoComplete: 'address-line2',
  hint: 'Met toevoeging, bijv. 12a'
}, {
  key: 'street',
  label: 'Straat',
  autoComplete: 'address-line1'
}, {
  key: 'city',
  label: 'Plaats',
  autoComplete: 'address-level2'
}];
const lookupHint: Partial<Record<LookupState, string>> = {
  loading: 'Adres zoeken…',
  found: 'Aangevuld via je postcode. Klopt het niet? Pas het gewoon aan.',
  notFound: 'Niet automatisch gevonden. Vul straat en plaats zelf in.'
};

/** Eén rustige kaart per stap. De volgorde is echt: eerst wie, dan waarheen, dan hoe je betaalt. */
function Step({
  n,
  title,
  aside,
  children
}: {
  n: number;
  title: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return <section className="bg-shell p-5 ring-1 ring-line sm:p-7" aria-labelledby={`co-step-${n}`} data-id="element-6955" data-mp="2comizylixor2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-6956" data-mp="2cm9v74t34k63">
        <h2 id={`co-step-${n}`} className="flex items-baseline gap-3 font-display text-xl text-ink" data-id="element-6957" data-mp="2hllbkcpjhnn5">
          <span aria-hidden="true" className="text-[14px] text-clay" data-id="element-6958" data-mp="2mkfsf93y524m">
            {n}
          </span>
          {title}
        </h2>
        {aside}
      </div>
      <div className="mt-5" data-id="element-6959" data-mp="2wbafugsxi7rn">{children}</div>
    </section>;
}
const CHECKOUT_FORM_ID = 'checkout-form';
const SUBMIT_ID = 'checkout-submit';

/** Contact, bezorgadres, betaling, notitie en de bestelknop. */
export function CheckoutForm({
  c
}: {
  c: CheckoutControl;
}) {
  const {
    openCart
  } = useOverlayActions();
  const {
    remove
  } = useCartActions();
  const {
    blockedLines
  } = c;
  const count = c.cart.lines.reduce((n, l) => n + l.qty, 0);
  const checks = useFieldChecks({
    email: c.email,
    ...c.address
  }, c.fieldErrors, c.address.country);
  // Het voorbeeld volgt het gekozen land.
  const hintOf = (f: AddressField) => {
    if (f.key === 'postcode') return `Bijv. ${postcodeExample[c.address.country]}`;
    if (f.key === 'street') return lookupHint[c.lookup];
    return f.hint;
  };
  const describe = (key: string, hintId?: string) => {
    const error = checks.errorOf(key);
    const ids = [error ? fieldErrorId(key) : null, hintId].filter(Boolean).join(' ');
    return {
      'aria-invalid': Boolean(error) || undefined,
      'aria-describedby': ids || undefined
    };
  };
  const fieldError = (key: string) => {
    const error = checks.errorOf(key);
    return error ? <p id={fieldErrorId(key)} className="mt-1.5 text-[13px] text-clay" data-id="element-6960" data-mp="1rdypfv7djitd">
        {error}
      </p> : null;
  };
  return <form id={CHECKOUT_FORM_ID} className="lg:col-span-7" onSubmit={checks.guardSubmit(c.placeOrder)} aria-busy={c.submitting} noValidate data-id="element-6961" data-mp="1ax4n6efd59ee">
      <Link to="/webshop" className="inline-flex items-center gap-1.5 py-1 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-6962" data-mp="1fv6874wcnisi">
        <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6963" data-mp="16xm4lpb0laeq" />
        Verder winkelen
      </Link>
      <h1 className="mt-4 font-display text-4xl font-light text-ink sm:text-5xl" data-id="element-6964" data-mp="1umowdpzd4jmy">Afrekenen</h1>
      <p className="mt-2 text-[15px] text-muted" data-id="element-6965" data-mp="1ymgybju93rjc">Drie korte stappen. Een account is niet nodig.</p>

      {/* Op de telefoon staat de samenvatting onder het formulier: hier alvast wat je bestelt en voor hoeveel. */}
      <a href="#co-summary" className="mt-6 flex items-center justify-between gap-4 bg-shell px-4 py-3 text-[14px] text-ink ring-1 ring-line lg:hidden" data-id="element-6966" data-mp="1ptm19dext24n">
        <span data-id="element-6967" data-mp="2ibzo6hyqj3oy">
          Je bestelling · {plural(count, 'product', 'producten')}
          <span className="block text-[13px] text-muted underline underline-offset-4" data-id="element-6968" data-mp="2rqb84v5gmjdn">Bekijk overzicht</span>
        </span>
        <span className="font-display text-xl" data-id="element-6969" data-mp="2nprazrkvzfri">{euroCents(c.cart.total)}</span>
      </a>

      <div className="mt-8 space-y-4 sm:space-y-5" data-id="element-6970" data-mp="31gw2u7i0j3ra">
        <Step n={1} title="Contact" aside={<p className="text-[13px] text-muted" data-id="element-6972" data-mp="2wikww7uu9dec">
              {c.account ? `Ingelogd als ${c.account.customer.firstName}` : <Link to="/account" className="underline underline-offset-4 hover:text-ink" data-id="element-6973" data-mp="24qloun4fak5c">
                  Al een account? Log in
                </Link>}
            </p>} data-id="element-6971" data-mp="31igyk0g2csqq">
          <label className={labelCls} htmlFor={fieldId('email')} data-id="element-6974" data-mp="2dqik57lbih9d">
            E-mailadres
          </label>
          <input id={fieldId('email')} type="email" inputMode="email" required autoComplete="email" value={c.email} onChange={e => {
          c.setEmail(e.target.value);
          checks.change('email', e.target.value);
        }} onBlur={() => checks.blur('email')} className={inputCls} {...describe('email', 'co-email-hint')} data-id="element-6975" data-mp="3u3z06wayd8fy" />
          {fieldError('email')}
          <p id="co-email-hint" className="mt-1.5 text-[13px] text-muted" data-id="element-6976" data-mp="ezysol3l6vpy">
            Voor je bevestiging en de track & trace.
          </p>
        </Step>

        <Step n={2} title="Bezorgadres" data-id="element-6977" data-mp="3ihqk9ydt9dnm">
          {/* Zichtbare labels in plaats van alleen placeholders: na invullen (of autofill) zie je nog wat waar staat. */}
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-6978" data-mp="3nfikdfom062w">
            <div className="sm:col-span-2" data-id="element-6979" data-mp="3s81v84vwwn40">
              <label htmlFor={fieldId('country')} className={labelCls} data-id="element-6980" data-mp="3s8uyuwzkqwhx">
                Land
              </label>
              {/* Native keuzelijst: het land bepaalt de postcodecontrole en, op de server, de verzendkosten. */}
              <select id={fieldId('country')} autoComplete="country" value={c.address.country} onChange={e => c.setCountry(e.target.value as Country)} className={inputCls} data-id="element-6981" data-mp="2s7q1j3lbhok6">
                {countries.map(o => <option key={o.value} value={o.value} data-id="element-6982" data-mp="2dapirpl3t3xu">
                    {o.label}
                  </option>)}
              </select>
            </div>
            {addressFields.map(f => {
            const hint = hintOf(f);
            const hintId = hint ? `${fieldId(f.key)}-hint` : undefined;
            const error = checks.errorOf(f.key);
            return <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''} data-id="element-6983" data-mp="1uk7t5tvkiogp">
                  <label htmlFor={fieldId(f.key)} className={labelCls} data-id="element-6984" data-mp="1ui4wws3tnz3j">
                    {f.label}
                  </label>
                  <input id={fieldId(f.key)} required autoComplete={f.autoComplete}
              // Postcode: hoofdletters en geen autocorrectie (anders maakt de telefoon van "1234 AB" iets anders).
              autoCapitalize={f.key === 'postcode' ? 'characters' : undefined} spellCheck={f.key === 'postcode' || f.key === 'houseNumber' ? false : undefined} value={c.address[f.key]} onChange={e => {
                c.setAddressField(f.key, e.target.value);
                checks.change(f.key, e.target.value);
              }} onBlur={() => checks.blur(f.key)} className={inputCls} {...describe(f.key, !error ? hintId : undefined)} data-id="element-6985" data-mp="1lirmnx7vj6hr" />
                  {fieldError(f.key)}
                  {hint && !error &&
              // De aanvulling meldt zich rustig aan schermlezers; de andere hints niet.
              <p id={hintId} className="mt-1.5 text-[13px] text-muted" aria-live={f.key === 'street' ? 'polite' : undefined} data-id="element-6986" data-mp="2brpezda8tezh">
                      {hint}
                    </p>}
                </div>;
          })}
          </div>
        </Step>

        <Step n={3} title="Betaling" data-id="element-6987" data-mp="225eclyg20rcc">
          <fieldset data-id="element-6988" data-mp="2228mqgzlplfk">
            <legend className="sr-only" data-id="element-6989" data-mp="1x5rzrq451d42">Kies hoe je betaalt</legend>
            <div className="divide-y divide-line border border-line" data-id="element-6990" data-mp="1x7ml35jbn78o">
              {paymentOptionsFor(c.address.country).map(p => {
              const on = c.payment === p.method;
              return <label key={p.method}
              // Het echte keuzerondje is onzichtbaar; toon de toetsenbordfocus op de hele regel.
              className={`flex cursor-pointer items-center gap-4 px-4 py-4 transition-colors duration-150 ease-soft [&:has(:focus-visible)]:-outline-offset-2 [&:has(:focus-visible)]:outline [&:has(:focus-visible)]:outline-2 [&:has(:focus-visible)]:outline-clay ${on ? 'bg-sand/70' : 'hover:bg-sand/40'}`} data-id="element-6991" data-mp="271v9bh1bke11">
                    <input type="radio" name="payment" checked={on} onChange={() => c.setPayment(p.method)} className="sr-only" data-id="element-6992" data-mp="2ago2auzwnxd6" />
                    <span aria-hidden="true" className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors duration-150 ease-soft ${on ? 'border-ink' : 'border-ink/25'}`} data-id="element-6993" data-mp="26fkk41udxp8s">
                      {on && <span className="h-2 w-2 rounded-full bg-ink" data-id="element-6994" data-mp="2y45taobcirf3" />}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-6995" data-mp="2u32iig85nlkq">
                      <span className="block text-[15px] text-ink" data-id="element-6996" data-mp="2p4iso4qfxpn3">{p.label}</span>
                      <span className="block text-[13px] text-muted sm:hidden" data-id="element-6997" data-mp="2k7ilfqhuugue">{p.note}</span>
                    </span>
                    <span className="hidden text-right text-[13px] text-muted sm:block" data-id="element-6998" data-mp="1drgfljnjvi7o">{p.note}</span>
                  </label>;
            })}
            </div>
          </fieldset>
        </Step>

        {/* Optioneel, dus geen stapnummer: rustig onder de drie stappen. */}
        <div className="px-1 pt-2" data-id="element-6999" data-mp="13nrr1owmxcoc">
          <label htmlFor={fieldId('skinNote')} className="font-display text-lg text-ink" data-id="element-7000" data-mp="13km6mmyyayyy">
            Iets wat {founder.firstName} moet weten? <span className="font-sans text-[14px] text-muted" data-id="element-7001" data-mp="1dd1mneqotdh7">(optioneel)</span>
          </label>
          <textarea id={fieldId('skinNote')} maxLength={1000} value={c.skinNote} onChange={e => c.setSkinNote(e.target.value)} rows={3} placeholder="Bijv. ik ben zwanger, mijn huid reageert snel op parfum…" aria-describedby="co-skinNote-hint" className={`${inputCls} mt-3 resize-none`} data-id="element-7002" data-mp="1hedc9k9k9ype" />
          <p id="co-skinNote-hint" className="mt-2 text-[13px] text-muted" data-id="element-7003" data-mp="3t3xm1tsjmy73">
            Ik lees elke bestelling zelf en pas het gebruiksadvies erop aan.
          </p>

          <label className="mt-6 flex cursor-pointer items-start gap-4" data-id="element-7004" data-mp="1zh210vop5ek">
            <input type="checkbox" checked={c.remind} onChange={e => c.setRemind(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-ink" data-id="element-7005" data-mp="3q2l0t3cdf3u5" />
            <span data-id="element-7006" data-mp="3ul3r1az5jd4">
              <span className="block text-[15px] text-ink" data-id="element-7007" data-mp="3v4n8mg9wft9">Stuur me een seintje als mijn product bijna op is</span>
              <span className="mt-1 block text-[14px] leading-relaxed text-muted" data-id="element-7008" data-mp="3v04bxudrv0hv">Eén bericht per product. Geen nieuwsbrief, geen abonnement.</span>
            </span>
          </label>
        </div>
      </div>

      <div ref={c.errorRef} tabIndex={-1} className="outline-none" data-id="element-7009" data-mp="7vojzowxphev">
        <FormError message={c.error} className="mt-8" data-id="element-7010" data-mp="mlvr58hhre09" />
      </div>

      {blockedLines.length > 0 && <div role="alert" className="mt-8 border border-clay/40 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-7011" data-mp="hlzlz5x25gj4">
          <p data-id="element-7012" data-mp="vmtzg2yyq7on">
            {blockedLines.length === 1 ? 'Dit product is' : 'Deze producten zijn'} net uitverkocht. Haal {blockedLines.length === 1 ? 'het' : 'ze'} weg om af te rekenen.
          </p>
          {/* Direct hier weghalen, zonder eerst de winkelmand te openen. */}
          <ul className="mt-2 space-y-1" data-id="element-7013" data-mp="1f82kw57t5wne">
            {blockedLines.map(l => <li key={l.product.id} className="flex flex-wrap items-center justify-between gap-2" data-id="element-7014" data-mp="118b6w4hakg9e">
                <span data-id="element-7015" data-mp="166mjuo2vv6vu">{l.product.name}</span>
                <button type="button" onClick={() => remove(l.product.id)} className="py-1 text-ink underline underline-offset-4" data-id="element-7016" data-mp="165kwbk4okrx8">
                  Weghalen<span className="sr-only" data-id="element-7017" data-mp="w8yfd6fh6l0h">: {l.product.name}</span>
                </button>
              </li>)}
          </ul>
          <p className="mt-2 text-[13px] text-muted" data-id="element-7018" data-mp="iil7t56hx8bo">
            Liever een alternatief?{' '}
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4" data-id="element-7019" data-mp="mk6neyaf00pk">
              App {founder.firstName}
              <span className="sr-only" data-id="element-7020" data-mp="digwdtkg33sb">{shopLabels.viaWhatsApp}</span>
            </a>{' '}
            of{' '}
            <button type="button" onClick={openCart} aria-haspopup="dialog" className="text-ink underline underline-offset-4" data-id="element-7021" data-mp="1fvmukkgmjwrg">
              open je winkelmand
            </button>
            .
          </p>
        </div>}

      <button id={SUBMIT_ID} type="submit" disabled={c.submitting || blockedLines.length > 0} className={`${btnPrice} mt-8 ${c.submitting ? 'disabled:cursor-wait' : ''}`} data-id="element-7022" data-mp="172jes5iib7tn">
        {c.submitting ? <span className="flex w-full items-center justify-center gap-2" data-id="element-7023" data-mp="x3anxlixu9bi">
            <Spinner data-id="element-7024" data-mp="s4r01fksangi" />
            Even geduld, je bestelling wordt geplaatst…
          </span> : <>
            <span className={btnPriceLabel} data-id="element-7025" data-mp="o6jtfdk1b2j1">
              <LockIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7026" data-mp="j6dtla3m29pv" />
              {/* Wettelijk: de knop moet duidelijk maken dat er een betalingsverplichting ontstaat. */}
              Bestellen en betalen
            </span>
            <span className={btnPriceAmount} data-id="element-7027" data-mp="2dhkdgiyx243x">{euroCents(c.cart.total)}</span>
          </>}
      </button>
      <CheckoutTrust data-id="element-7028" data-mp="2q296bztyc199" />
      {/* Niet tijdens het versturen: dan toont de knop onderaan de voortgang. */}
      {!c.submitting && <CheckoutPayBar formId={CHECKOUT_FORM_ID} submitId={SUBMIT_ID} totalCents={c.cart.total} disabled={blockedLines.length > 0} data-id="element-7029" data-mp="2uspizhpy7iuk" />}
    </form>;
}